import { faInfo, faPlusCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { ErrorMessage, useFormikContext } from 'formik';
import React from 'react';
import { Button, Card, Col, Form, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { CreateApplicationFormValues } from '../../views/ApplicationCreate';
import ApplicationCourseCreate from './ApplicationCourseCreate';
import { RootState } from '../../store/rootReducer';

const ApplicationEducationCreate = (): React.ReactElement => {
  const { t } = useTranslation(['createApplication', 'common']);
  const { educationIndustries, educationIndustriesLoading, educationIndustriesError } = useSelector((state: RootState) => state.createApplication);
  const { educationOthers, educationOthersLoading, educationOthersError } = useSelector((state: RootState) => state.createApplication);
  const { values, errors, touched, getFieldProps, setFieldValue } = useFormikContext<CreateApplicationFormValues>();

  return (
    <Card className="mb-2">
      <Card.Body>
        <Row>
          <Col>
            <h4>{t('education')}</h4>
          </Col>
        </Row>
        <Row>
          <Col md="4">
            <p className="small text-muted">{t('educationHelp')}</p>
          </Col>
          <Col md="8">
            {' '}
            <Form.Row>
              <Col>
                <Form.Label>{t('isMatriculationFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isMatriculationFinished-yes"
                  name="isMatriculationFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isMatriculationFinished === true}
                  onChange={(e) => setFieldValue('isMatriculationFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isMatriculationFinished-no"
                  name="isMatriculationFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isMatriculationFinished === false}
                  onChange={(e) => setFieldValue('isMatriculationFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isMatriculationFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
              <Col>
                <Form.Label>{t('isContractFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isContractFinished-yes"
                  name="isContractFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isContractFinished === true}
                  onChange={(e) => setFieldValue('isContractFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isContractFinished-no"
                  name="isContractFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isContractFinished === false}
                  onChange={(e) => setFieldValue('isContractFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isContractFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
              <Col>
                <Form.Label>{t('hasOngoingContract')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="hasOngoingContract-yes"
                  name="hasOngoingContract"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.hasOngoingContract === true}
                  onChange={(e) => setFieldValue('hasOngoingContract', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="hasOngoingContract-no"
                  name="hasOngoingContract"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.hasOngoingContract === false}
                  onChange={(e) => setFieldValue('hasOngoingContract', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="hasOngoingContract" component="div" className="invalid-feedback d-block text-left" />
              </Col>
            </Form.Row>
            <br />
            <Form.Row>
              <Col md="4">
                <Form.Label>{t('isJourneymanFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isJourneymanFinished-yes"
                  name="isJourneymanFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isJourneymanFinished === true}
                  onChange={(e) => setFieldValue('isJourneymanFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isJourneymanFinished-no"
                  name="isJourneymanFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isJourneymanFinished === false}
                  onChange={(e) => setFieldValue('isJourneymanFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isJourneymanFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
              <Col md="4">
                <Form.Label>{t('isEducationAbroadFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isEducationAbroadFinished-yes"
                  name="isEducationAbroadFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isEducationAbroadFinished === true}
                  onChange={(e) => setFieldValue('isEducationAbroadFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isEducationAbroadFinished-no"
                  name="isEducationAbroadFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isEducationAbroadFinished === false}
                  onChange={(e) => setFieldValue('isEducationAbroadFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isEducationAbroadFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
            </Form.Row>
            <br />
            {values.isJourneymanFinished && (
              <Form.Group controlId="educationIndustry">
                <Form.Label>{t('educationIndustry')}*:</Form.Label>
                <Select
                  options={
                    educationIndustries
                      ? educationIndustries.map((e) => ({
                          value: e.id,
                          label: e.name,
                        }))
                      : []
                  }
                  isMulti
                  value={values.educationIndustry}
                  onChange={(v) => setFieldValue('educationIndustry', v)}
                  placeholder={t('chooseEducationIndustry')}
                  className={classNames({
                    'is-invalid': touched.educationIndustry && errors.educationIndustry,
                  })}
                />
                {educationIndustriesError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
                {educationIndustriesLoading === 'idle' && educationIndustries?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noSchools')}</div>}
                <Form.Text className="text-muted">{educationIndustriesError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
                <ErrorMessage name="educationIndustry" component="div" className="invalid-feedback d-block text-left" />
              </Form.Group>
            )}
            <Form.Group controlId="educationOther">
              <Form.Label>{t('educationOther')}:</Form.Label>
              <Select
                options={
                  educationOthers
                    ? educationOthers.map((e) => ({
                        value: e.id,
                        label: e.name,
                      }))
                    : []
                }
                isMulti
                value={values.educationOther}
                onChange={(v) => setFieldValue('educationOther', v)}
                placeholder={t('chooseEducationOther')}
              />
              {educationOthersError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
              {educationOthersLoading === 'idle' && educationOthers?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noEducation')}</div>}
              <Form.Text className="text-muted">{educationOthersError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
              <ErrorMessage name="educationOther" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <OverlayTrigger placement="right-end" overlay={<Tooltip id="tooltip-courses">{t('EducationCoursesInfo')}</Tooltip>}>
                  <div>
                    {t('educationCourses')}: <FontAwesomeIcon icon={faInfo} />
                  </div>
                </OverlayTrigger>
              </Form.Label>
              {values.course.map((_, i) => (
                <ApplicationCourseCreate key={`course-${i}`} index={i} />
              ))}

              <Button
                type="button"
                variant="secondary"
                className="float-right"
                onClick={(e) =>
                  setFieldValue('course', [
                    ...values.course,
                    {
                      name: '',
                    },
                  ])
                }
              >
                <FontAwesomeIcon icon={faPlusCircle} /> {t('addCourse')}
              </Button>
            </Form.Group>
            <Form.Group controlId="otherEducationInformation">
              <Form.Label>
                <OverlayTrigger placement="right-end" overlay={<Tooltip id="tooltip-other">{t('EducationOtherInfo')}</Tooltip>}>
                  <div>
                    {t('otherEducationInformation')}: <FontAwesomeIcon icon={faInfo} />
                  </div>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                type="text"
                className={classNames({
                  'is-invalid': touched.otherEducationInformation && errors.otherEducationInformation,
                })}
                {...getFieldProps('otherEducationInformation')}
              />
              <ErrorMessage name="otherEducationInformation" component="div" className="invalid-feedback d-block text-left" />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
export default ApplicationEducationCreate;
